import { styled } from "@mui/material/styles";
import type { ComponentProps } from "react";

/**
 * How wide a cell's control slot is, in pixels: a small (20px) icon inside a 4px pad on each side. The
 * slot never shrinks below it, so an icon lands on the same line in every cell of a column whatever the
 * value beside it says. A cell has no edit icon of its own: focusing the value opens it (#6624).
 */
export const REDLINE_CELL_ACTION_SLOT_WIDTH = 28;
/** The space the slot keeps between itself and the value, in pixels. */
export const REDLINE_CELL_ACTION_GAP = 4;

/** Class names on the three parts of a redline cell, for tests and for styles that reach into one. */
export const redlineCellClasses = {
  root: "RedlineCell-root",
  value: "RedlineCell-value",
  actionSlot: "RedlineCell-actionSlot",
} as const;

/**
 * One editable cell of a redline table: the value on the left, the edit control on the right.
 *
 * Every editable column of the fee schedule used to place its pencil its own way — straight after text
 * of any width in three of them, after a field with a minimum width in the fee, and at the far edge at
 * a smaller size in the team member (#5405). The pencils then sat wherever each value happened to end,
 * and moved again when a diff put the old and the new text side by side. This is the one shape they all
 * take now: the value in {@link RedlineCellValue}, which takes the room there is and cuts off what does
 * not fit, and the control in {@link RedlineCellActionSlot} at the cell's trailing edge.
 *
 * It lays out inside whatever width the table gives the column; it declares none of its own, so the
 * table keeps its automatic layout.
 */
const CellRoot = styled("div")({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
  width: "100%",
});

export function RedlineCell({ className, ...props }: Readonly<ComponentProps<typeof CellRoot>>) {
  return <CellRoot className={withClass(redlineCellClasses.root, className)} {...props} />;
}

/** The flexible part of a {@link RedlineCell}: the value or the diff, cut off with an ellipsis. */
const CellValue = styled("div")({
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export function RedlineCellValue({ className, ...props }: Readonly<ComponentProps<typeof CellValue>>) {
  return <CellValue className={withClass(redlineCellClasses.value, className)} {...props} />;
}

/**
 * The fixed part of a {@link RedlineCell}: the slot a cell's controls (its history, say) sit in, at
 * the trailing edge.
 *
 * `minWidth` rather than `width` so a cell with more than one control still fits them; one small icon
 * button fills it exactly.
 *
 * The auto margin holds it to the trailing edge even where the value stops short of the cell: a value
 * with a cap of its own (the description's) in a column the table has widened to share out spare room.
 */
const CellActionSlot = styled("div")({
  alignItems: "center",
  boxSizing: "border-box",
  display: "flex",
  flex: "0 0 auto",
  justifyContent: "flex-end",
  marginLeft: "auto",
  minWidth: REDLINE_CELL_ACTION_SLOT_WIDTH + REDLINE_CELL_ACTION_GAP,
  paddingLeft: REDLINE_CELL_ACTION_GAP,
});

export function RedlineCellActionSlot({ className, ...props }: Readonly<ComponentProps<typeof CellActionSlot>>) {
  return <CellActionSlot className={withClass(redlineCellClasses.actionSlot, className)} {...props} />;
}

function withClass(partClass: string, className: string | undefined): string {
  return className ? `${partClass} ${className}` : partClass;
}
